import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import {useNavigate} from 'react-router';

import {DEFAULT_CARD} from '@/app/main/onboarding/constants/OnboardingConstants';
import {useLoading} from '@/app/zustand/useLoading';
import {featureFlags} from '@/config/featureFlags';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';
import logError from '@/utils/errors/logError';

import {IKid} from '../../model/AddKidMode';
import {getKidData} from '../../services/handleKidInformation';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  kidData: Partial<IKid>;
  handleDurationEnds: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

export const ViewModelProvider = ({children}: {children: ReactNode}) => {
  //#region [---- [PROPERTIES] ----]
  const navigate = useNavigate();
  const kidData = getKidData();
  const {trackViewAddKidConfirmationSuccess} = useTrackers(kidData?.uuid);
  const {setSkipLoading, stopLoading} = useLoading();
  const isEnabledRequestPhysicalCard =
    featureFlags.isEnabledRequestPhysicalCard;
  //#endregion

  //#region [---- [REPOSITORIES] ----]
  const {profile, refetchProfile} = useUsersRepository();
  //#endregion

  const selectedTheme =
    profile?.metadata?.ONBOARDING_CARD_THEME_SELECTED || DEFAULT_CARD;

  const isWebViewActive = useMemo(() => {
    return (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    );
  }, []);

  const handleSendWebViewMessage = useCallback(() => {
    stopLoading();
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        status: WEB_VIEW_MESSAGES.CONTINUE_ONBOARDING,
        kidUuid: kidData?.uuid,
        choseDesign: selectedTheme,
        previousScreen: 'addKidKidCreated viewModelContext',
      }),
    );
  }, [kidData?.uuid, selectedTheme, stopLoading]);

  //#region [---- [HOOKS] ----]
  useEffect(() => {
    if (WebViewService.isWebViewActive()) setSkipLoading(true);
    trackViewAddKidConfirmationSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //#endregion

  //#region [---- [LOGIC] ----]
  const tryRefreshProfile = useCallback(async () => {
    try {
      await refetchProfile();
    } catch (error) {
      logError(error, {
        fileName: 'KidCreated ViewModelContext',
        functionName: 'tryRefreshProfile',
      });
    }
  }, [refetchProfile]);

  const handleDurationEnds = useCallback(async () => {
    await tryRefreshProfile();

    setSkipLoading(false);
    if (!isWebViewActive) {
      if (isEnabledRequestPhysicalCard) {
        navigate('/onboarding/request-physical-card');
        return;
      }

      navigate('/onboarding/download-app');
    }

    if (isWebViewActive) {
      handleSendWebViewMessage();
    }
  }, [
    handleSendWebViewMessage,
    isEnabledRequestPhysicalCard,
    isWebViewActive,
    navigate,
    tryRefreshProfile,
    setSkipLoading,
  ]);
  //#endregion

  return (
    <ViewModelContext.Provider value={{kidData, handleDurationEnds}}>
      {children}
    </ViewModelContext.Provider>
  );
};

export const useViewModelProvider = () => {
  const context = useContext(ViewModelContext);
  if (!context) {
    throw new Error('KidCreated ViewModelProvider is not found.');
  }
  return context;
};
